import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const FORMATS = [
  "Professional stand-up showcases",
  "PG-13 shows",
  "Adult comedy shows",
  "Corporate-appropriate shows",
  "Hosted comedy events",
  "Customized comedian lineups",
];

const CLIENTS = [
  "Breweries",
  "Restaurants",
  "Lounges",
  "Businesses",
  "Theaters",
  "Customer appreciation events",
  "Corporate gatherings",
  "Private events",
  "Special events",
];

export default function ComedyPage() {
  return (
    <Layout padTop>
      <Seo
        title="Live Comedy Shows for Venues | Hustle Promotions"
        description="Book professional live stand-up comedy for your venue, brewery, restaurant or private event. Hustle Promotions builds the comedian lineup around your audience and handles the show."
      />

      {/* HERO */}
      <section data-testid="comedy-hero" style={{ background: "var(--black)" }}>
        <div className="container-editorial section-pad">
          <div className="split-hero">
            <div>
              <Reveal>
                <p className="eyebrow" style={{ marginBottom: "1.5rem" }}>Live Stand-Up Comedy</p>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="h-page" data-testid="comedy-h1" style={{ maxWidth: "15ch" }}>
                  Bring Live Comedy To Your Venue.
                </h1>
              </Reveal>
              <Reveal delay={140}>
                <p className="lede" data-testid="comedy-availability" style={{ marginTop: "1.75rem" }}>
                  Contact Hustle Promotions regarding comedy booking availability.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <div className="flex flex-col sm:flex-row sm:flex-wrap" style={{ gap: "1rem", marginTop: "2.5rem" }}>
                  <Link to="/book" className="btn btn-solid" data-testid="comedy-book-cta">
                    Book A Comedy Show <ArrowRight size={16} strokeWidth={2} />
                  </Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <div className="media-frame media-tall">
                <img
                  src="/assets/comedy-laughing-ropes.jpg"
                  alt="A stand-up comedian laughing while leaning on the ring ropes during a live set, black and white"
                  className="img-cover"
                  style={{ objectPosition: "60% 50%" }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FULL-WIDTH FEATURE BAND */}
      <section data-testid="comedy-feature" className="relative">
        <div className="hairline" />
        <div className="media-band">
          <img
            src="/assets/comedy-polo.jpg"
            alt="A stand-up comedian performing with a microphone inside the ring, black and white"
            className="img-cover obj-center-bottom"
            style={{ position: "absolute", inset: 0 }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.78) 100%)",
            }}
          />
          <div
            className="container-editorial relative"
            style={{ height: "100%", display: "flex", alignItems: "flex-end", paddingTop: "2rem", paddingBottom: "clamp(2rem, 5vw, 3.5rem)" }}
          >
            <Reveal>
              <div>
                <p className="eyebrow" style={{ marginBottom: "0.85rem" }}>Real Performers</p>
                <h2 className="h-section" style={{ maxWidth: "18ch" }}>
                  Comedians Who Command The Room.
                </h2>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="hairline" />
      </section>

      {/* FORMATS + NETWORK */}
      <section data-testid="comedy-formats" style={{ background: "var(--off-black)" }}>
        <div className="container-editorial section-pad">
          <div className="split-hero" style={{ alignItems: "start" }}>
            <div>
              <Reveal>
                <p className="eyebrow">Formats</p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="h-section" style={{ maxWidth: "14ch", marginTop: "1rem" }}>
                  Built For Your Room.
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="body-md" data-testid="comedy-network" style={{ maxWidth: "46ch", marginTop: "1.5rem" }}>
                  Hustle Promotions has access to a broad network of comedians and builds the lineup
                  around the venue and audience.
                </p>
              </Reveal>
            </div>
            <div className="spec-grid cols-2" data-testid="comedy-formats-list">
              {FORMATS.map((item, i) => (
                <Reveal key={item} delay={i * 40}>
                  <div className="spec-item">
                    <span className="spec-mark">{String(i + 1).padStart(2, "0")}</span>
                    <span className="spec-text">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO RHYTHM — asymmetric */}
      <section data-testid="comedy-gallery" style={{ background: "var(--black)" }}>
        <div className="container-editorial section-pad">
          <Reveal>
            <p className="eyebrow" style={{ marginBottom: "1.75rem" }}>On The Mic</p>
          </Reveal>
          <div className="duo-grid">
            <Reveal>
              <div className="media-frame media-wide">
                <img
                  src="/assets/comedy-smile-jacket.jpg"
                  alt="A stand-up comedian in a jacket smiling mid-set with a microphone, black and white"
                  className="img-cover obj-center-top"
                />
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="media-frame media-wide">
                <img
                  src="/assets/comedy-glasses-cap.jpg"
                  alt="A stand-up comedian in glasses and a cap performing with a microphone, black and white"
                  className="img-cover obj-center-top"
                />
              </div>
            </Reveal>
          </div>
          <div className="split-hero" style={{ marginTop: "clamp(0.75rem, 1.5vw, 1rem)", alignItems: "stretch" }}>
            <Reveal>
              <div className="media-frame media-tall" style={{ height: "100%" }}>
                <img
                  src="/assets/comedy-vertical-hoodie.webp"
                  alt="A full-body shot of a stand-up comedian in a hoodie performing in the ring, black and white"
                  className="img-cover obj-left-bottom"
                />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", paddingTop: "1rem" }}>
                <h2 className="h-section" style={{ maxWidth: "14ch" }}>
                  One Lineup, Built Around You.
                </h2>
                <p className="body-md" style={{ maxWidth: "44ch", marginTop: "1.5rem" }}>
                  These are real performers from Hustle Promotions shows. Every booking is assembled to
                  match your audience, your room and the tone you want for the night.
                </p>
                <div style={{ marginTop: "2rem" }}>
                  <Link to="/book" className="link-underline" data-testid="comedy-gallery-cta">
                    Book A Comedy Show <ArrowRight className="arrow" size={16} strokeWidth={2} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLIENT TYPES */}
      <section data-testid="comedy-clients" style={{ background: "var(--off-black)" }}>
        <div className="hairline" />
        <div className="container-editorial section-pad">
          <div className="split-hero" style={{ alignItems: "start" }}>
            <div>
              <Reveal>
                <p className="eyebrow">Who We Work With</p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="h-section" style={{ maxWidth: "14ch", marginTop: "1rem" }}>
                  Where Comedy Lands.
                </h2>
              </Reveal>
            </div>
            <div className="spec-grid cols-2" data-testid="comedy-clients-list">
              {CLIENTS.map((c) => (
                <Reveal key={c} delay={30}>
                  <div className="spec-item">
                    <span className="spec-mark">—</span>
                    <span className="spec-text">{c}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="comedy-cta" style={{ background: "var(--black)" }}>
        <div className="hairline" />
        <div className="container-editorial section-pad">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between" style={{ gap: "2rem" }}>
            <Reveal>
              <h2 className="h-section" style={{ maxWidth: "18ch" }}>
                Put Live Comedy On Your Calendar.
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <Link to="/book" className="btn btn-solid" data-testid="comedy-cta-book">
                Book A Comedy Show <ArrowRight size={16} strokeWidth={2} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </Layout>
  );
}
